import React, { useState, useEffect } from 'react';

import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { Box, IconButton, Typography } from '@mui/material';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';

import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import { useLoader } from '../../provider/LoaderProvider';
import useUsers from '../../context/UsersContext';
import { ButtonUI, DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import EmployeeAbsenceFields from './EmployeeAbsenceFields';
import EmployeeAbsenceReports from './EmployeeAbsenceReports';
import useMenus from '../../context/MenuContext';
import employeeAbsenceService from '../../service/employeeAbsence.service';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import { EmployeeAbsenceApprovalStatus } from '../../config/employeeAbsenceRequest';
import { useParams } from 'react-router-dom';
import appService from '../../service/app.service';

export const EmployeeAbsencePage = () => {
  const { id } = useParams();
  const { getUser, user } = useUsers();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [defaultValues, setDefaultValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    employee: [],
    user: [],
  });

  const def = {
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()),
    employeeId: null,
    mainCategory: null,
    subCategory: null,
    description: '',
    approvalStatus: EmployeeAbsenceApprovalStatus.PENDING,
    approverId: null,
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('employeeAbsence');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    employeeId: Yup.string().required(),
    mainCategory: Yup.string().required(),
    subCategory: Yup.string().nullable(),
    description: Yup.string().nullable(),
    approvalStatus: Yup.string().nullable(),
    approverId: Yup.string().required(),
  });

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    getRowById();
  }, [id]);

  const getRowById = () => {
    if (!id) return;

    employeeAbsenceService.getAbsenceById(id).then((data) => {
      setSelectedValues(data);
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          employee: resp[1],
        };

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          employee: generateAutocompleteItems(aData.employee, 'name', 'id'),
        };

        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const { startDate, endDate, employeeId, mainCategory, subCategory, description, approvalStatus, approverId } =
      selectedValues;

    setInitValues({
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      employeeId,
      mainCategory,
      subCategory,
      description: description ?? '',
      approvalStatus,
      approverId,
    });
  };

  const handleInsertObject = (values, resetForm) => {
    showLoader();
    const insertObject = {
      ...values,
      startDate: dayjs(values.startDate).startOf('day').add(12, 'hours').toISOString(),
      endDate: dayjs(values.endDate).startOf('day').add(12, 'hours').toISOString(),
    };

    employeeAbsenceService
      .createAbsence(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      startDate: dayjs(values.startDate).startOf('day').add(12, 'hours').toISOString(),
      endDate: dayjs(values.endDate).startOf('day').add(12, 'hours').toISOString(),
    };

    employeeAbsenceService.updateAbsence(updData, selectedValues.id).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleRemoveElement = async () => {
    employeeAbsenceService.deleteAbsence(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  const handleUpdateReadAt = () => {
    appService.updateReadAt(selectedValues.message.id).then(() => {
      getRowById();
    });
  };

  return (
    <>
      <FormBase
        name="EmployeeAbsence"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center flex-wrap justify-between">
                <FormHeaderUI
                  addClick={() => {
                    setSelectedValues(defaultSelectedValues);
                    setViewField(false);
                    setAddField(false);
                  }}
                  addDisabled={!viewField || !canEdit}
                  modClick={() => {
                    setViewField(false);
                    setEditButtonvisible(false);
                  }}
                  modDisabled={!viewField || operatorsDisabled || !canEdit}
                  deleteClick={() => {
                    setShowDeleteDialog(true);
                  }}
                  deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                  copyClick={() => {
                    setViewField(false);
                    setAddField(false);
                  }}
                  copyDisabled={!viewField || operatorsDisabled || !canEdit}
                />
                {id && selectedValues?.message && (
                  <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={handleUpdateReadAt}>
                    <IconButton>
                      <MarkChatReadIcon />
                    </IconButton>
                    <Typography>Elolvastam</Typography>
                  </Box>
                )}
              </div>
              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <EmployeeAbsenceFields
                      disabled={viewField}
                      selectedValues={selectedValues}
                      autoCompleteOptions={autoCompleteOptions}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <EmployeeAbsenceReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
